/**
 * AEO Impact Dashboard Service
 *
 * API client for fetching AEO impact comparison data.
 */

import { authFetch } from '@/utils/auth-fetch';

// =========================================================================
// Types
// =========================================================================

export interface AEOImpactSummary {
  totalPublished: number;
  totalDeployed: number;
  deploymentRate: number;
  catalogCoverage: number;
  totalCatalog: number;
}

export interface OrganicDailyPoint {
  date: string;
  totalOrganic: number;
  deployedOrganic: number;
  pctOnEnriched: number;
  deployedUrls: number;
}

export interface OrganicImpact {
  daily: OrganicDailyPoint[];
  trend: number | null;
}

export interface GroupMetrics {
  products: number;
  totalImpressions: number;
  totalClicks: number;
  avgCtr: number;
  avgPosition: number;
}

export interface GSCBucketRow {
  bucket: string;
  group: string;
  products: number;
  totalImpressions: number;
  totalClicks: number;
  avgCtr: number;
  avgPosition: number;
}

export interface GSCComparison {
  aggregate: Record<string, GroupMetrics>;
  buckets: GSCBucketRow[];
}

export interface GA4GroupMetrics {
  products: number;
  totalSessions: number;
  avgBounceRate: number;
  avgEngagementRate: number;
  avgTimeOnPage: number;
  totalConversions: number;
  totalRevenue: number;
  revenuePerSession: number;
}

export interface GA4BucketRow {
  bucket: string;
  group: string;
  products: number;
  totalSessions: number;
  avgBounceRate: number;
  avgEngagementRate: number;
  avgTimeOnPage: number;
  totalConversions: number;
  totalRevenue: number;
}

export interface GA4Comparison {
  aggregate: Record<string, GA4GroupMetrics>;
  buckets: GA4BucketRow[];
}

export interface AdsGroupMetrics {
  products: number;
  totalClicks: number;
  totalConversions: number;
  conversionRate: number;
  avgCpc: number;
}

export interface AdsComparison {
  aggregate: Record<string, AdsGroupMetrics>;
  qualityDistribution: Record<string, Record<string, number>>;
}

export interface AIVisibility {
  available: boolean;
  message: string;
}

export interface AEOImpactData {
  summary: AEOImpactSummary;
  organicImpact: OrganicImpact;
  gscComparison: GSCComparison;
  ga4Comparison: GA4Comparison;
  adsComparison: AdsComparison;
  deploymentStatus: Record<string, number>;
  aiVisibility: AIVisibility;
}

// =========================================================================
// Service
// =========================================================================

class AEOImpactService {
  async getImpactData(siteId: string, options: { startDate?: string; endDate?: string } = {}): Promise<AEOImpactData> {
    const params = new URLSearchParams();
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);

    const qs = params.toString();
    const url = `/api/aeo-impact/${siteId}${qs ? `?${qs}` : ''}`;
    const response = await authFetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to fetch AEO impact data (${response.status})`);
    }
    return response.json();
  }
}

export const aeoImpactService = new AEOImpactService();
